// ============================================================================
// PROCESS CHECKLIST PANEL
// Unified checklist panel showing template-based steps + work breakdown forms.
// Replaces ResolveChecklistPanel in WorkItemDetailModal.
// Date: April 2026
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/service-operations/process-gates.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/service-operations/process-gates)
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  CheckCircle2,
  Circle,
  Shield,
  ShieldOff,
  Loader2,
  ChevronDown,
  ChevronRight,
  FileSignature,
  User,
  Building2,
  AlertTriangle,
  ClipboardCheck,
  Info,
  MessageSquare,
  Clock,
} from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import { authService } from '@/lib/authService';
import notify from '@/lib/notifications';
import DynamicFormModal from '@/components/forms/DynamicFormModal';
import UserDocsLink from '@/components/shared/UserDocsLink';

// ============================================================================
// TYPES
// ============================================================================

interface StepCompletion {
  completed_by_user_id: number;
  completed_by_name: string | null;
  completed_at: string;
  comment: string | null;
  is_undone: boolean;
}

interface GateStep {
  id: number;
  step_order: number;
  title: string;
  description: string | null;
  assigned_role: string | null;
  assigned_department: string | null;
  assigned_departments?: string[];
  assigned_user_id: number | null;
  assigned_user_ids?: number[];
  assigned_user_name: string | null;
  assigned_user_names?: string[];
  assigned_employee_id: number | null; // legacy
  allow_comments: boolean;
  comments_required: boolean;
  is_optional: boolean;
  can_complete: boolean;
  completion: StepCompletion | null;
}

interface ProcessGate {
  instance_id: number;
  template_id: number;
  template_name: string;
  trigger_on_status: string | null;
  trigger_on_statuses?: string[];
  block_on_status?: string | null;
  block_on_statuses?: string[];
  is_enforced: boolean;
  blocks_target_status?: boolean;
  status: 'open' | 'complete' | 'released';
  gate_released: boolean;
  released_by: number | null;
  released_at: string | null;
  release_reason: string | null;
  summary: { total: number; completed: number; optional: number };
  steps: GateStep[];
}

interface FormsGate {
  enabled: boolean;
  enforced: boolean;
  total: number;
  completed: number;
  gate_released: any;
}

interface ResolveForm {
  id: number;
  form_key: string;
  title: string;
  description: string | null;
  entity_type: string | null;
  latest_submission: null | {
    id: number;
    overall_status: string | null;
    submitted_at: string | null;
    completion_percentage: number | null;
    form_data: Record<string, any>;
    required_field_total: number;
    required_field_completed: number;
    is_ready_for_resolution: boolean;
  };
}

interface ProcessGateData {
  forms_gate: FormsGate;
  process_gates: ProcessGate[];
  has_any_gate: boolean;
  has_process_gate_templates: boolean;
  is_complete: boolean;
  is_blocked: boolean;
}

interface ProcessGatePanelProps {
  workItemId: string;
  workItemType?: string;
  targetStatus?: string | null;
  onComplete?: () => void;
  onSummaryChange?: (summary: { total: number; completed: number; percentage: number; isComplete: boolean; isBlocked: boolean }) => void;
  onConfigureInForms?: () => void;
  onConfigureGates?: () => void;
}

function getAuthToken(): string | null {
  return authService.getTokens()?.access_token || null;
}

function formatAt(dateString: string | null): string {
  if (!dateString) return '';
  try {
    const d = new Date(dateString);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function getRoleLabel(role: string | null, department: string | null, userName: string | null): string {
  if (userName) return userName;
  const parts: string[] = [];
  if (department) parts.push(department);
  if (role) parts.push(role.charAt(0).toUpperCase() + role.slice(1));
  return parts.join(' · ') || 'Anyone';
}

function normalizeStringArrayValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry || '').trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry || '').trim()).filter(Boolean);
      }
    } catch {
      // fall through
    }

    return trimmed.includes(',')
      ? trimmed.split(',').map((entry) => entry.trim()).filter(Boolean)
      : [trimmed];
  }

  return [];
}

function humanizeStatusLabel(value: string): string {
  return value
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatStatusList(values: string[]): string {
  return values.map(humanizeStatusLabel).join(', ');
}

function getEffectiveBlockStatuses(gate: Pick<ProcessGate, 'block_on_status' | 'block_on_statuses' | 'trigger_on_status' | 'trigger_on_statuses' | 'is_enforced'>): string[] {
  if (!gate.is_enforced) {
    return [];
  }

  const explicitBlockStatuses = normalizeStringArrayValue(gate.block_on_statuses ?? gate.block_on_status);
  if (explicitBlockStatuses.length > 0) {
    return explicitBlockStatuses;
  }

  return normalizeStringArrayValue(gate.trigger_on_statuses ?? gate.trigger_on_status);
}

// ============================================================================
// STEP ROW
// ============================================================================

function StepRow({
  step,
  instanceId,
  workItemId: _workItemId,
  isDark,
  onToggle,
  toggling,
}: {
  step: GateStep;
  instanceId: number;
  workItemId: string;
  isDark: boolean;
  onToggle: (instanceId: number, stepId: number, complete: boolean, comment?: string) => Promise<void>;
  toggling: boolean;
}) {
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const isComplete = Boolean(step.completion && !step.completion.is_undone);
  const canOpenOptionalCommentPanel = !isComplete && step.can_complete && step.allow_comments && !step.comments_required;
  const assignedDepartments = normalizeStringArrayValue(step.assigned_departments ?? step.assigned_department);
  const assignedLabel = step.assigned_user_names?.length
    ? step.assigned_user_names.join(', ')
    : getRoleLabel(step.assigned_role, assignedDepartments.join(', '), step.assigned_user_name);

  const handleCheckboxClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
    event?.stopPropagation();

    if (!step.can_complete && !isComplete) return;

    if (!isComplete && step.comments_required) {
      setShowComment(true);
      return;
    }

    void onToggle(instanceId, step.id, !isComplete, comment.trim() || undefined);
  };

  const handleCardClick = () => {
    if (!canOpenOptionalCommentPanel || showComment) {
      return;
    }

    setShowComment(true);
  };

  const handleSubmitWithComment = () => {
    if (step.comments_required && !comment.trim()) return;
    void onToggle(instanceId, step.id, true, comment.trim() || undefined);
    setShowComment(false);
    setComment('');
  };

  const handleUncomplete = () => {
    void onToggle(instanceId, step.id, false, undefined);
  };

  return (
    <div className={`rounded-lg border transition-colors ${
      isComplete
        ? isDark ? 'border-green-800/50 bg-green-900/10' : 'border-green-200 bg-green-50/60'
        : isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'
    }`}>
      <div
        className={`flex items-start gap-3 p-3 ${canOpenOptionalCommentPanel ? 'cursor-pointer' : ''}`}
        onClick={handleCardClick}
      >
        {/* Checkbox / Status icon */}
        <div className="mt-0.5 flex-shrink-0">
          {toggling ? (
            <Loader2 className="h-4.5 w-4.5 animate-spin text-blue-500" />
          ) : isComplete ? (
            <button
              onClick={(event) => {
                event.stopPropagation();
                handleUncomplete();
              }}
              title="Mark as incomplete"
              className="group"
            >
              <CheckCircle2 className={`h-5 w-5 text-green-500 group-hover:text-amber-500 transition-colors`} />
            </button>
          ) : (
            <button
              onClick={handleCheckboxClick}
              disabled={!step.can_complete}
              title={!step.can_complete ? `Only ${assignedLabel} can complete this step` : step.comments_required ? 'Add required comment and complete' : 'Mark as complete'}
              className="disabled:cursor-not-allowed"
            >
              <Circle className={`h-5 w-5 transition-colors ${
                step.can_complete
                  ? isDark ? 'text-gray-500 hover:text-blue-400' : 'text-gray-400 hover:text-blue-600'
                  : isDark ? 'text-gray-700' : 'text-gray-300'
              }`} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div>
              <span className={`text-sm font-medium leading-snug ${
                isComplete
                  ? isDark ? 'text-green-300 line-through' : 'text-green-700 line-through'
                  : isDark ? 'text-gray-200' : 'text-gray-800'
              }`}>
                {step.title}
                {step.is_optional && (
                  <span className={`ml-1.5 text-[10px] font-normal ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>(optional)</span>
                )}
              </span>
              {step.description && !isComplete && (
                <p className={`mt-0.5 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{step.description}</p>
              )}
            </div>
            {/* Assigned label */}
            {assignedLabel !== 'Anyone' && (
              <span className={`flex-shrink-0 flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full ${
                isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'
              }`}>
                {step.assigned_user_id ? <User className="h-2.5 w-2.5" /> : <Building2 className="h-2.5 w-2.5" />}
                {assignedLabel}
              </span>
            )}
          </div>

          {/* Completion info */}
          {isComplete && step.completion && (
            <div className={`mt-1 flex items-center gap-1.5 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              <Clock className="h-3 w-3" />
              <span>{step.completion.completed_by_name || 'Unknown'}</span>
              <span>·</span>
              <span>{formatAt(step.completion.completed_at)}</span>
              {step.completion.comment && (
                <>
                  <span>·</span>
                  <MessageSquare className="h-3 w-3" />
                  <span className="italic truncate max-w-[12rem]">{step.completion.comment}</span>
                </>
              )}
            </div>
          )}

          {/* Comment box (appears on first click if comments allowed) */}
          {showComment && !isComplete && (
            <div className="mt-2 space-y-2">
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder={step.comments_required ? 'Comment required…' : 'Add a comment (optional)…'}
                rows={2}
                className={`w-full rounded-md border px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 ${
                  isDark
                    ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500'
                    : 'border-gray-300 bg-white text-gray-800 placeholder-gray-400'
                }`}
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSubmitWithComment}
                  disabled={step.comments_required && !comment.trim()}
                  className="px-3 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Complete
                </button>
                <button
                  onClick={() => { setShowComment(false); setComment(''); }}
                  className={`px-3 py-1 text-xs font-medium rounded-md ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProcessGatePanel({
  workItemId,
  targetStatus = null,
  onComplete: _onComplete,
  onSummaryChange,
  onConfigureInForms,
  onConfigureGates,
}: ProcessGatePanelProps) {
  const { currentTheme } = useTheme();
  const isDark = currentTheme.mode === 'dark';

  const emitChecklistUpdated = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.dispatchEvent(new CustomEvent('processChecklistUpdated', {
      detail: {
        workItemId: String(workItemId),
        timestamp: Date.now(),
      },
    }));
  }, [workItemId]);

  // Track whether gate has been in a blocked state during this panel session,
  // so we can auto-call onComplete the moment it transitions to unblocked.
  const hasBeenBlockedRef = useRef(false);
  const onCompleteRef = useRef(_onComplete);
  useEffect(() => { onCompleteRef.current = _onComplete; }, [_onComplete]);

  const [data, setData] = useState<ProcessGateData | null>(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null); // 'instanceId:stepId'
  const [releasingGate, setReleasingGate] = useState<number | null>(null); // instanceId
  const [showReleaseReason, setShowReleaseReason] = useState<number | null>(null);
  const [releaseReason, setReleaseReason] = useState('');
  const [expandedGates, setExpandedGates] = useState<Set<number>>(new Set());
  const [showFormModal, setShowFormModal] = useState(false);
  const [activeFormId, setActiveFormId] = useState<number | null>(null);
  const [resolveForms, setResolveForms] = useState<ResolveForm[]>([]);
  const prevSummaryRef = useRef<string>('');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const token = getAuthToken();
      // Load process gates (unified)
      const gatesQuery = targetStatus
        ? `?target_status=${encodeURIComponent(targetStatus)}`
        : '';
      const gatesRes = await fetch(`/api/v1/work-items/${workItemId}/process-gates${gatesQuery}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (gatesRes.ok) {
        const gatesJson = await gatesRes.json();
        if (gatesJson.success) {
          // Detect blocked→unblocked transition and notify parent
          const isNowBlocked = Boolean(gatesJson.data.is_blocked);
          if (isNowBlocked) {
            hasBeenBlockedRef.current = true;
          } else if (hasBeenBlockedRef.current) {
            hasBeenBlockedRef.current = false;
            // Defer so state settles before parent acts
            setTimeout(() => onCompleteRef.current?.(), 0);
          }
          setData(gatesJson.data);
          // Expand all gates by default
          const ids = new Set<number>((gatesJson.data.process_gates as ProcessGate[]).map((g) => g.instance_id));
          setExpandedGates(ids);
        }
      }

      // If forms gate is enabled, also load resolve-checklist for form details
      const checklistRes = await fetch(`/api/v1/work-items/${workItemId}/resolve-checklist`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (checklistRes.ok) {
        const checklistJson = await checklistRes.json();
        if (checklistJson.success && checklistJson.data?.enabled) {
          const forms: ResolveForm[] = [];
          if (Array.isArray(checklistJson.data.resolve_forms)) {
            forms.push(...checklistJson.data.resolve_forms);
          } else if (checklistJson.data.resolve_form) {
            forms.push(checklistJson.data.resolve_form);
          }
          setResolveForms(forms);
        }
      }
    } catch (err) {
      console.error('[ProcessGatePanel] load error:', err);
    } finally {
      setLoading(false);
    }
  }, [targetStatus, workItemId]);

  useEffect(() => {
    void load();
  }, [load]);

  // Notify parent of summary changes
  useEffect(() => {
    if (!data || !onSummaryChange) return;
    const totalProcessSteps = data.process_gates.reduce((sum, g) => sum + g.summary.total, 0);
    const completedProcessSteps = data.process_gates.reduce((sum, g) => sum + g.summary.completed, 0);
    const formsTotal = data.forms_gate.total;
    const formsCompleted = data.forms_gate.completed;
    const total = totalProcessSteps + formsTotal;
    const completed = completedProcessSteps + formsCompleted;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    const summaryKey = `${total}-${completed}`;
    if (summaryKey !== prevSummaryRef.current) {
      prevSummaryRef.current = summaryKey;
      onSummaryChange({ total, completed, percentage, isComplete: data.is_complete, isBlocked: data.is_blocked });
    }
  }, [data, onSummaryChange]);

  const handleToggleStep = useCallback(async (
    instanceId: number,
    stepId: number,
    complete: boolean,
    comment?: string,
  ) => {
    const key = `${instanceId}:${stepId}`;
    setToggling(key);
    try {
      const res = await fetch(`/api/v1/work-items/${workItemId}/process-gates`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: complete ? 'complete_step' : 'uncomplete_step',
          instance_id: instanceId,
          step_id: stepId,
          comment: comment || undefined,
        }),
      });
      const json = await res.json();
      if (!json.success) {
        notify.error(json.error || 'Failed to update step');
        return;
      }
      await load();
      emitChecklistUpdated();
    } catch {
      notify.error('Failed to update step');
    } finally {
      setToggling(null);
    }
  }, [emitChecklistUpdated, workItemId, load]);

  const handleGateRelease = async (instanceId: number, action: 'release_gate' | 'revoke_release') => {
    setReleasingGate(instanceId);
    try {
      const res = await fetch(`/api/v1/work-items/${workItemId}/process-gates`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action,
          instance_id: instanceId,
          reason: action === 'release_gate' ? releaseReason.trim() || undefined : undefined,
        }),
      });
      const json = await res.json();
      if (json.success) {
        notify.success(action === 'release_gate' ? 'Gate released — item can now proceed' : 'Gate release revoked');
        setShowReleaseReason(null);
        setReleaseReason('');
        await load();
        emitChecklistUpdated();
      } else {
        notify.error(json.error || 'Failed to update gate');
      }
    } catch {
      notify.error('Failed to update gate');
    } finally {
      setReleasingGate(null);
    }
  };

  const handleFormsGateRelease = async (action: 'release_gate' | 'revoke_gate') => {
    try {
      const res = await fetch(`/api/v1/work-items/${workItemId}/resolve-checklist`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason: releaseReason.trim() || undefined }),
      });
      const json = await res.json();
      if (json.success) {
        notify.success(action === 'release_gate' ? 'Forms gate released' : 'Forms gate release revoked');
        setShowReleaseReason(null);
        setReleaseReason('');
        await load();
        emitChecklistUpdated();
      } else {
        notify.error(json.error || 'Failed to update gate');
      }
    } catch {
      notify.error('Failed to update forms gate');
    }
  };

  const handleFormSubmit = useCallback(async (formValues: Record<string, any>) => {
    const selectedForm = resolveForms.find((f) => f.id === activeFormId) || resolveForms[0];
    if (!selectedForm) throw new Error('Form configuration missing');

    const res = await fetch('/api/v1/forms/submissions', {
      method: 'POST',
      headers: { Authorization: `Bearer ${getAuthToken()}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({
        form_configuration_id: selectedForm.id,
        form_key: selectedForm.form_key,
        work_item_id: workItemId,
        entity_type: selectedForm.entity_type || 'work_item',
        entity_id: workItemId,
        form_data: formValues,
        overall_status: 'submitted',
      }),
    });
    const result = await res.json().catch(() => null);
    if (!res.ok || !result?.success) throw new Error(result?.error || 'Failed to submit form');

    notify.success('Form submitted successfully');
    setShowFormModal(false);
    await load();
    emitChecklistUpdated();
  }, [activeFormId, emitChecklistUpdated, resolveForms, workItemId, load]);

  // ── Loading ──
  if (loading) {
    return (
      <div className={`flex items-center justify-center py-10 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
        <Loader2 className="h-5 w-5 animate-spin mr-2" />
        <span className="text-sm">Loading process checklist…</span>
      </div>
    );
  }

  if (!data || !data.has_any_gate) {
    return (
      <div className={`rounded-lg border p-5 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-start gap-2">
          <Info className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <div className="space-y-3">
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              No process checklists are configured for this work item type yet.
            </p>
            <div className="flex gap-2 flex-wrap">
              {onConfigureGates && (
                <button
                  onClick={onConfigureGates}
                  data-tour-id="process-gate-configure-btn"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                >
                  <Shield className="h-3.5 w-3.5" />
                  Configure Process Checklists
                </button>
              )}
              {onConfigureInForms && (
                <button
                  onClick={onConfigureInForms}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  <FileSignature className="h-3.5 w-3.5" />
                  Set Up Work Breakdown Forms
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activeResolveForm = resolveForms.find((f) => f.id === activeFormId) || resolveForms[0];
  const totalAllSteps = data.process_gates.reduce((s, g) => s + g.summary.total, 0) + data.forms_gate.total;
  const completedAllSteps = data.process_gates.reduce((s, g) => s + g.summary.completed, 0) + data.forms_gate.completed;
  const overallPct = totalAllSteps > 0 ? Math.round((completedAllSteps / totalAllSteps) * 100) : 0;

  return (
    <div className="space-y-4" data-tour-id="process-gate-panel">
      {/* ── Overall progress header ── */}
      <div data-tour-id="process-gate-progress" className={`rounded-lg border p-4 ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50'}`}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <ClipboardCheck className={`h-5 w-5 ${data.is_complete ? 'text-green-500' : isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <h3 className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-700'}`}>
              Process Checklist
            </h3>
            <UserDocsLink docsPath="/total-operations/workflows/service-operations/process-gates" label="User guide: Process gates" />
          </div>
          <span className={`text-sm font-bold ${data.is_complete ? 'text-green-500' : isDark ? 'text-blue-400' : 'text-blue-600'}`}>
            {overallPct}%
          </span>
        </div>
        <div className={`w-full rounded-full h-2 ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
          <div
            className={`h-2 rounded-full transition-all duration-300 ${data.is_complete ? 'bg-green-500' : 'bg-blue-600'}`}
            style={{ width: `${overallPct}%` }}
          />
        </div>
        <p className={`text-xs mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
          {completedAllSteps} of {totalAllSteps} item{totalAllSteps !== 1 ? 's' : ''} complete
          {data.is_blocked && (
            <span className="ml-2 text-amber-500 font-medium">· Required for a status transition</span>
          )}
          {data.is_complete && completedAllSteps >= totalAllSteps && totalAllSteps > 0 && (
            <span className="ml-2 text-green-500 font-medium">· All requirements met</span>
          )}
          {!data.is_blocked && completedAllSteps < totalAllSteps && totalAllSteps > 0 && (
            <span className="ml-2 text-blue-500 font-medium">· Advisory only</span>
          )}
        </p>
      </div>

      {/* ── Template-based gates ── */}
      <div data-tour-id="process-gate-templates">
      {data.process_gates.map((gate) => {
        const isExpanded = expandedGates.has(gate.instance_id);
        const requiredSteps = gate.steps.filter((s) => !s.is_optional);
        const completedRequired = requiredSteps.filter((s) => s.completion && !s.completion.is_undone).length;
        const gateComplete = gate.status === 'complete' || gate.status === 'released';
        const showReleaseForm = showReleaseReason === gate.instance_id;

        return (
          <div key={gate.instance_id} className={`rounded-lg border overflow-hidden ${
            gateComplete
              ? isDark ? 'border-green-800/50' : 'border-green-200'
              : gate.is_enforced
                ? isDark ? 'border-gray-600' : 'border-gray-300'
                : isDark ? 'border-gray-700' : 'border-gray-200'
          }`}>
            {/* Gate header */}
            <button
              onClick={() => setExpandedGates((prev) => {
                const next = new Set(prev);
                if (next.has(gate.instance_id)) next.delete(gate.instance_id);
                else next.add(gate.instance_id);
                return next;
              })}
              className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                isDark ? 'bg-gray-800/60 hover:bg-gray-700/60' : 'bg-gray-50 hover:bg-gray-100/80'
              }`}
            >
              {isExpanded
                ? <ChevronDown className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                : <ChevronRight className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
              }
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                    {gate.template_name}
                  </span>
                  {normalizeStringArrayValue(gate.trigger_on_statuses ?? gate.trigger_on_status).length > 0 && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'}`}>
                      shows on {formatStatusList(normalizeStringArrayValue(gate.trigger_on_statuses ?? gate.trigger_on_status))}
                    </span>
                  )}
                  {gate.is_enforced && !gateComplete && (() => {
                    const blockStatuses = getEffectiveBlockStatuses(gate);

                    return (
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                        {blockStatuses.length > 0
                          ? `blocks ${formatStatusList(blockStatuses)}`
                          : 'blocks all transitions'}
                      </span>
                    );
                  })()}
                  {gate.status === 'released' && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-orange-900/30 text-orange-300' : 'bg-orange-100 text-orange-700'}`}>
                      Released
                    </span>
                  )}
                </div>
              </div>
              {/* Progress badge */}
              <span className={`flex-shrink-0 text-[11px] font-semibold ${
                gateComplete ? 'text-green-500' : isDark ? 'text-gray-400' : 'text-gray-500'
              }`}>
                {completedRequired}/{requiredSteps.length}
              </span>
            </button>

            {/* Steps */}
            {isExpanded && (
              <div className={`p-3 space-y-2.5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                {gate.steps.length === 0 ? (
                  <p className={`text-xs text-center py-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    No steps configured for this checklist.
                  </p>
                ) : (
                  gate.steps.map((step) => (
                    <StepRow
                      key={step.id}
                      step={step}
                      instanceId={gate.instance_id}
                      workItemId={workItemId}
                      isDark={isDark}
                      onToggle={handleToggleStep}
                      toggling={toggling === `${gate.instance_id}:${step.id}`}
                    />
                  ))
                )}

                {/* Gate release section (admin) */}
                {gate.is_enforced && !gateComplete && (
                  <div className={`mt-3 rounded-lg border p-3 ${isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'}`}>
                    {showReleaseForm ? (
                      <div className="space-y-2">
                        <p className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                          Release reason (optional):
                        </p>
                        <textarea
                          value={releaseReason}
                          onChange={(e) => setReleaseReason(e.target.value)}
                          placeholder="e.g. Emergency closure — follow-up scheduled"
                          rows={2}
                          className={`w-full rounded-md border px-2.5 py-1.5 text-xs resize-none focus:outline-none focus:ring-1 focus:ring-amber-500 ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500' : 'border-gray-300 bg-white text-gray-800 placeholder-gray-400'}`}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => void handleGateRelease(gate.instance_id, 'release_gate')}
                            disabled={releasingGate === gate.instance_id}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-md hover:bg-amber-600 disabled:opacity-50"
                          >
                            {releasingGate === gate.instance_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
                            Release Checklist
                          </button>
                          <button
                            onClick={() => { setShowReleaseReason(null); setReleaseReason(''); }}
                            className={`px-3 py-1.5 text-xs font-medium rounded-md ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowReleaseReason(gate.instance_id)}
                        className={`flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-gray-400 hover:text-amber-400' : 'text-gray-400 hover:text-amber-600'}`}
                      >
                        <ShieldOff className="h-3.5 w-3.5" />
                        Release checklist (admin override)
                      </button>
                    )}
                  </div>
                )}

                {/* Revoke release */}
                {gate.status === 'released' && (
                  <div className={`mt-2 rounded-lg border p-3 ${isDark ? 'border-orange-800/40 bg-orange-900/10' : 'border-orange-200 bg-orange-50'}`}>
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                        <ShieldOff className="h-3.5 w-3.5" />
                        <span className="font-medium">Checklist released</span>
                        {gate.release_reason && (
                          <span className="opacity-70">· {gate.release_reason}</span>
                        )}
                      </div>
                      <button
                        onClick={() => void handleGateRelease(gate.instance_id, 'revoke_release')}
                        disabled={releasingGate === gate.instance_id}
                        className={`text-xs px-2 py-1 rounded font-medium transition-colors ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                      >
                        Revoke
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
      </div>

      {/* ── Forms gate section ── */}
      {data.forms_gate.enabled && (
        <div data-tour-id="process-gate-forms" className={`rounded-lg border overflow-hidden ${
          data.forms_gate.total > 0 && data.forms_gate.completed >= data.forms_gate.total
            ? isDark ? 'border-green-800/50' : 'border-green-200'
            : isDark ? 'border-gray-600' : 'border-gray-300'
        }`}>
          <div className={`flex items-center gap-3 px-4 py-3 ${isDark ? 'bg-gray-800/60' : 'bg-gray-50'}`}>
            <FileSignature className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div className="min-w-0 flex-1">
              <span className={`text-sm font-semibold ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                Work Breakdown Forms
              </span>
              {data.forms_gate.enforced && (
                <span className={`ml-2 text-[10px] px-1.5 py-0.5 rounded font-medium ${isDark ? 'bg-amber-900/30 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                  Required
                </span>
              )}
            </div>
            <span className={`flex-shrink-0 text-[11px] font-semibold ${
              data.forms_gate.total > 0 && data.forms_gate.completed >= data.forms_gate.total
                ? 'text-green-500' : isDark ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {data.forms_gate.completed}/{data.forms_gate.total}
            </span>
          </div>

          <div className={`p-3 space-y-2.5 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
            {resolveForms.length === 0 ? (
              <div className="flex items-start gap-2 text-sm py-1">
                <AlertTriangle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                <div className="space-y-2">
                  <span className={isDark ? 'text-gray-400' : 'text-gray-500'}>
                    No process checklist forms have been configured in Work Breakdown yet.
                  </span>
                  {onConfigureInForms && (
                    <button
                      onClick={onConfigureInForms}
                      className="block text-xs font-medium text-blue-600 hover:text-blue-700"
                    >
                      Open Forms Builder →
                    </button>
                  )}
                </div>
              </div>
            ) : (
              resolveForms.map((form) => {
                const sub = form.latest_submission;
                const isReady = Boolean(sub?.is_ready_for_resolution);
                return (
                  <div key={form.id} className={`rounded-lg border p-3 ${
                    isReady
                      ? isDark ? 'border-green-800/50 bg-green-900/10' : 'border-green-200 bg-green-50/60'
                      : isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-white'
                  }`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          {isReady
                            ? <CheckCircle2 className="h-4 w-4 flex-shrink-0 text-green-500" />
                            : <Circle className={`h-4 w-4 flex-shrink-0 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                          }
                          <span className={`text-sm font-medium ${isDark ? 'text-gray-200' : 'text-gray-800'}`}>
                            {form.title}
                          </span>
                        </div>
                        {sub && (
                          <p className={`mt-1 ml-6 text-[11px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            {isReady ? 'Checklist-ready' : 'Required fields missing'} · {formatAt(sub.submitted_at)}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => { setActiveFormId(form.id); setShowFormModal(true); }}
                        className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-2.5 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
                      >
                        <FileSignature className="h-3 w-3" />
                        {sub ? 'Update' : 'Complete'}
                      </button>
                    </div>
                  </div>
                );
              })
            )}

            {/* Forms gate release */}
            {data.forms_gate.enabled && data.forms_gate.enforced && data.forms_gate.total > 0
              && data.forms_gate.completed < data.forms_gate.total && (
              <div className={`mt-1 rounded-lg border p-3 ${
                data.forms_gate.gate_released
                  ? isDark ? 'border-orange-800/40 bg-orange-900/10' : 'border-orange-200 bg-orange-50'
                  : isDark ? 'border-gray-700 bg-gray-800/30' : 'border-gray-200 bg-gray-50'
              }`}>
                {data.forms_gate.gate_released ? (
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-orange-300' : 'text-orange-700'}`}>
                      <ShieldOff className="h-3.5 w-3.5" />
                      <span className="font-medium">Forms checklist released</span>
                    </div>
                    <button
                      onClick={() => void handleFormsGateRelease('revoke_gate')}
                      className={`text-xs px-2 py-1 rounded font-medium ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                    >
                      Revoke
                    </button>
                  </div>
                ) : (
                  showReleaseReason === -1 ? (
                    <div className="space-y-2">
                      <textarea
                        value={releaseReason}
                        onChange={(e) => setReleaseReason(e.target.value)}
                        placeholder="Release reason (optional)…"
                        rows={2}
                        className={`w-full rounded-md border px-2.5 py-1.5 text-xs resize-none ${isDark ? 'border-gray-600 bg-gray-700 text-gray-200 placeholder-gray-500' : 'border-gray-300 bg-white text-gray-800 placeholder-gray-400'}`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void handleFormsGateRelease('release_gate')}
                          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-amber-500 text-white rounded-md hover:bg-amber-600"
                        >
                          <ShieldOff className="h-3 w-3" />Release
                        </button>
                        <button onClick={() => { setShowReleaseReason(null); setReleaseReason(''); }}
                          className={`px-3 py-1.5 text-xs font-medium rounded-md ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}
                        >Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowReleaseReason(-1)}
                      className={`flex items-center gap-1.5 text-xs font-medium ${isDark ? 'text-gray-400 hover:text-amber-400' : 'text-gray-400 hover:text-amber-600'}`}
                    >
                      <ShieldOff className="h-3.5 w-3.5" />
                      Release forms checklist (admin override)
                    </button>
                  )
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Form modal for process checklist forms */}
      {activeResolveForm && (
        <DynamicFormModal
          isOpen={showFormModal}
          onClose={() => setShowFormModal(false)}
          formKey={activeResolveForm.form_key}
          title={activeResolveForm.title}
          subtitle="Complete this form to satisfy the process checklist requirement."
          mode={activeResolveForm.latest_submission ? 'edit' : 'create'}
          entityType={activeResolveForm.entity_type || 'work_item'}
          entityId={String(workItemId)}
          initialData={activeResolveForm.latest_submission?.form_data || {}}
          onSubmit={handleFormSubmit}
        />
      )}
    </div>
  );
}
